import { ComponentPropsWithoutRef, useCallback } from 'react';
import styled from 'styled-components';
import BoldAddIcon from '@paradigm/design-system/src/assets/BoldAddIcon';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';
import { venueSupportsCustomStrategy } from '@paradigm/features/src/unified-rfqs/entities/products/venue';
import { TOAST_TIMEOUT_SHORT, toast } from '#/components/UnifiedRfqs/toast';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import DefaultButton from '@paradigm/design-system/src/components/Button';
import { useTrackAction } from '@paradigm/features/src/analytics';

export default function LegsTableActions() {
  const view = useRfqBuilderView();
  const hasVenueSupport = venueSupportsCustomStrategy(
    view.venue.selectedOption.value,
  );

  return (
    <ActionsBar>
      <ClearAllLegsButton disabled={!hasVenueSupport} />
      <AddHedgeButton />
      <AddNewLegButton disabled={!hasVenueSupport} />
    </ActionsBar>
  );
}

function AddNewLegButton(props: ComponentPropsWithoutRef<'button'>) {
  const { addNewLeg } = useRfqBuilderActions();
  const { strategy, strategyShortcut } = useRfqBuilderView();
  const trackAction = useTrackAction();

  const strategyCode =
    strategyShortcut.selectedOption?.value ??
    strategy.selectedOption?.value ??
    null;

  const handleClick = useCallback(() => {
    trackAction('add strategy leg', { strategyCode });
    addNewLeg();
  }, [addNewLeg, strategyCode, trackAction]);

  return (
    <DefaultButton
      ariaLabel="add new leg"
      theme="secondary-dark-blue"
      icon={<BoldAddIcon width={8} />}
      {...props}
      onClick={handleClick}
    >
      Add New Leg
    </DefaultButton>
  );
}

function AddHedgeButton() {
  const { hedge, strategy, strategyShortcut } = useRfqBuilderView();
  const { addNewHedgeLeg } = useRfqBuilderActions();
  const trackAction = useTrackAction();

  const strategyCode =
    strategyShortcut.selectedOption?.value ??
    strategy.selectedOption?.value ??
    null;

  const onAddHedge = useCallback(() => {
    trackAction('add strategy leg', { strategyCode, isHedge: true });
    if (hedge.errorMsg != null) {
      toast(hedge.errorMsg, { autoClose: TOAST_TIMEOUT_SHORT });
      return;
    }

    addNewHedgeLeg(hedge);
  }, [addNewHedgeLeg, hedge, strategyCode, trackAction]);

  return (
    <DefaultButton
      ariaLabel="add hedge"
      theme="secondary-dark-blue"
      icon={<BoldAddIcon width={8} />}
      iconPlacement="left"
      onClick={onAddHedge}
      disabled={hedge.isDisabled}
    >
      Add Hedge
    </DefaultButton>
  );
}

function ClearAllLegsButton(props: ComponentPropsWithoutRef<'button'>) {
  const { clearAllLegs } = useRfqBuilderActions();
  const trackAction = useTrackAction();
  const { strategy, strategyShortcut } = useRfqBuilderView();

  const strategyCode =
    strategyShortcut.selectedOption?.value ??
    strategy.selectedOption?.value ??
    null;

  const handleClick = useCallback(() => {
    trackAction('remove strategy leg', { strategyCode, shouldRemoveAll: true });
    clearAllLegs();
  }, [clearAllLegs, strategyCode, trackAction]);

  return (
    <ClearButton ariaLabel="clear all" {...props} onClick={handleClick}>
      Clear All
    </ClearButton>
  );
}

const ClearButton = styled(DefaultButton).attrs({
  variant: 'link',
})`
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0;
  }
`;

const ActionsBar = styled.div`
  > * {
    display: inline-block;
    margin-left: 1rem;
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    gap: 0.75rem;

    > button {
      height: auto;
      margin: 0;
      display: flex;
      font-size: 1rem;
      line-height: 2rem;
      align-items: center;
    }
  }
`;
